<template>
    <div>
        <!-- Page Banner -->
        <PageBanner pageTitle="Contact Us" />
        
        <!-- Contact Form and Info Section -->
        <div class="container ptb-115">
            <div class="row pb-85">
                <!-- Contact Form -->
                <div class="col-lg-7 mb-lg-0 mb-4">
                    <h2 class="section-title style-one fw-semibold mb-40">{{ formTitle }}</h2>

                    <form @submit.prevent="submitForm" class="form-wrapper style-two">
                        <div class="row">
                            <div class="col-md-6">
                                <div class="form-group mb-20">
                                    <label for="name" class="d-blck text-title w-100 mb-8">Name*</label>
                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        v-model="formData.name"
                                        class="w-100 ht-54 text-para outline-0 round-5"
                                        placeholder="Your Name"
                                        required
                                    />
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="form-group mb-20">
                                    <label for="email" class="d-blck text-title w-100 mb-8">Email*</label>
                                    <input
                                        type="email"
                                        name="email"
                                        id="email"
                                        v-model="formData.email"
                                        class="w-100 ht-54 text-para outline-0 round-5"
                                        placeholder="your@email.com"
                                        required
                                    />
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="form-group mb-20">
                                    <label for="phone" class="d-blck text-title w-100 mb-8">Phone*</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        id="phone"
                                        v-model="formData.phone"
                                        class="w-100 ht-54 text-para outline-0 round-5"
                                        placeholder="+1 (555) 123-4567"
                                        required
                                    />
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="form-group mb-20">
                                    <label for="subject" class="d-blck text-title w-100 mb-8">Subject</label>
                                    <input
                                        type="text"
                                        name="subject"
                                        id="subject"
                                        v-model="formData.subject"
                                        class="w-100 ht-54 text-para outline-0 round-5"
                                        placeholder="Subject"
                                    />
                                </div>
                            </div>
                            <div class="col-12">
                                <div class="form-group mb-25">
                                    <label for="message" class="d-blck text-title w-100 mb-8">
                                        Your Message*
                                    </label>
                                    <textarea
                                        name="message"
                                        id="message"
                                        v-model="formData.message"
                                        class="w-100 ht-150 text-para outline-0 round-5 resize-0"
                                        placeholder="Please describe your inquiry..."
                                        required
                                    ></textarea>
                                </div>
                            </div>
                            <div class="col-12">
                                <button
                                    type="submit"
                                    class="btn style-one d-inline-flex flex-wrap align-items-center transition"
                                    :disabled="isSubmitting"
                                >
                                    <span
                                        class="btn-text d-flex flex-column align-items-center justify-content-center transition"
                                    >
                                        {{ isSubmitting ? 'Sending...' : 'Send Message' }}
                                    </span>
                                    <span
                                        class="btn-icon d-flex flex-column align-items-center justify-content-center transition"
                                    >
                                        <img :src="rightArrowIcon" alt="Icon" />
                                    </span>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                <!-- Contact Information -->
                <div class="col-xl-4 offset-xl-1 col-lg-5">
                    <div class="contact-info-box round-20">
                        <h5 class="fs-20 fw-semibold mb-20">Contact Information</h5>
                        
                        <div class="mb-20" v-if="contactEmail">
                            <h6 class="fs-16 fw-semibold mb-0">Email:</h6>
                            <a
                                :href="`mailto:${contactEmail}`"
                                class="text-para hover-text-secondary transition"
                            >
                                {{ contactEmail }}
                            </a>
                        </div>
                        
                        <div class="mb-20" v-if="contactPhone">
                            <h6 class="fs-16 fw-semibold mb-1">Phone:</h6>
                            <a
                                :href="`tel:${contactPhone.replace(/\D/g, '')}`"
                                class="text-para hover-text-secondary transition"
                            >
                                {{ contactPhone }}
                            </a>
                        </div>
                        
                        <div class="mb-20" v-if="socialLinks.length">
                            <h6 class="fs-16 fw-semibold mb-12">Follow Us:</h6>
                            <ul class="social-profile style-six list-unstyled mb-0">
                                <li v-for="(social, index) in socialLinks" :key="index">
                                    <a
                                        :href="social.link"
                                        target="_blank"
                                        class="d-flex flex-column align-items-center justify-content-center rounded-circle"
                                    >
                                        <i :class="social.icon"></i>
                                    </a>
                                </li>
                            </ul>
                        </div>
                        
                        <div v-if="address">
                            <h5 class="fs-20 fw-semibold mb-10">Our Office</h5>
                            <div class="mb-15">
                                <h6 class="fs-16 fw-semibold mb-1">Address</h6>
                                <p class="mb-0">{{ address }}</p>
                            </div>
                        </div>
                        
                        <div v-if="openingHours.weekday">
                            <h6 class="fs-16 fw-semibold mb-1">Office Hours</h6>
                            <p class="mb-0">{{ openingHours.weekday }}</p>
                            <p class="mb-0" v-if="openingHours.weekend">{{ openingHours.weekend }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Google Map -->
            <div class="comp-map style-one round-20" v-if="mapEmbedUrl">
                <iframe
                    :src="mapEmbedUrl"
                    style="border: 0; width: 100%; height: 100%"
                    allowfullscreen
                    loading="lazy"
                    referrerpolicy="no-referrer-when-downgrade"
                >
                </iframe>
            </div>
        </div>
    </div>
</template>

<script>
import { useSiteDefaultsStore } from '@/stores/siteDefaultsStore'
import PageBanner from '@/components/Common/PageBanner.vue'
import rightArrowIcon from '@/assets/img/icons/right-arrow.svg'

export default {
    name: 'Contact',
    components: {
        PageBanner
    },
    data() {
        return {
            formTitle: 'Send us a message',
            formData: {
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            },
            isSubmitting: false,
            rightArrowIcon
        }
    },
    computed: {
        siteDefaultsStore() {
            return useSiteDefaultsStore()
        },
        contactEmail() {
            return this.siteDefaultsStore.getContactEmail()
        },
        contactPhone() {
            return this.siteDefaultsStore.getContactPhone()
        },
        address() {
            return this.siteDefaultsStore.getAddress()
        },
        openingHours() {
            return this.siteDefaultsStore.getOpeningHours()
        },
        socialLinks() {
            return this.siteDefaultsStore.getSocialLinks() || []
        },
        mapEmbedUrl() {
            const lat = this.siteDefaultsStore.getMapLat()
            const lng = this.siteDefaultsStore.getMapLng()
            
            // If coordinates are available, use them
            if (lat && lng && lat !== '0' && lng !== '0') {
                return `https://www.google.com/maps/embed/v1/view?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&center=${lat},${lng}&zoom=15`
            }
            
            // Fallback to default location
            return 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3151.8385385572983!2d144.95358331584498!3d-37.81725074201705!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad65d4dd5a05d97%3A0x3e64f855a564844d!2s121%20King%20St%2C%20Melbourne%20VIC%203000%2C%20Australia!5e0!3m2!1sen!2sbd!4v1612419490850!5m2!1sen!2sbd'
        }
    },
    async mounted() {
        // Load contact-specific settings if available
        await this.loadContactSettings()
        
        // Set page title
        document.title = 'Contact Us - ' + (this.siteDefaultsStore.getSiteName() || 'DPMC')
    },
    methods: {
        async loadContactSettings() {
            try {
                const response = await this.$api.get('public/settings?section=contact')
                if (response.data && response.data.status === 'success') {
                    const data = response.data.data
                    
                    // Update form title if provided
                    if (data.form_title) {
                        this.formTitle = data.form_title
                    }
                }
            } catch (error) {
                console.error('Failed to load contact settings:', error)
                // Keep default values
            }
        },
        
        async submitForm() {
            if (this.isSubmitting) return
            
            this.isSubmitting = true
            
            try {
                // Submit feedback to CMS API
                const response = await this.$api.post('public/submit-feedback', {
                    name: this.formData.name,
                    email: this.formData.email,
                    phone: this.formData.phone,
                    subject: this.formData.subject,
                    message: this.formData.message
                })
                
                if (response.data && response.data.status === 'success') {
                    // Success - reset form
                    this.formData = {
                        name: '',
                        email: '',
                        phone: '',
                        subject: '',
                        message: ''
                    }
                    
                    this.$toast.success('Thank you! Your message has been sent successfully. We will get back to you soon.', 5000)
                } else {
                    throw new Error('Failed to submit form')
                }
            } catch (error) {
                console.error('Error submitting contact form:', error)
                this.$toast.error('There was an error sending your message. Please try again or contact us directly via email or phone.', 5000)
            } finally {
                this.isSubmitting = false
            }
        }
    }
}
</script>

<style scoped>
.contact-info-box {
    background: #f8f9fa;
    padding: 40px 30px;
}

.form-wrapper.style-two input,
.form-wrapper.style-two textarea {
    padding: 15px 20px;
    border: 1px solid #ddd;
}

.form-wrapper.style-two input:focus,
.form-wrapper.style-two textarea:focus {
    border-color: #6698C8;
    box-shadow: 0 0 0 0.2rem rgba(102, 152, 200, 0.15);
}

.social-profile.style-six li {
    display: inline-block;
    margin-right: 10px;
}

.social-profile.style-six li a {
    width: 40px;
    height: 40px;
    background: #6698C8;
    color: white;
    transition: all 0.3s ease;
}

.social-profile.style-six li a:hover {
    background: #303E4D;
    transform: translateY(-3px);
}

.comp-map {
    height: 500px;
    overflow: hidden;
}

@media (max-width: 767px) {
    .contact-info-box {
        padding: 30px 20px;
    }
    
    .comp-map {
        height: 350px;
    }
}
</style>
